/**
 * @file UsersList.jsx
 * @author Rishikesh
 * @date 2024-09-11
 * @description Show the list of users
 */
import React from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { ContentPasteSearchOutlined, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export const UsersList = ({ isLoading, users }) => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        rowGap={2}
      >
        <Stack direction={"row"} alignItems={"center"} columnGap={2}>
          {isLoading ? (
            <Skeleton variant="rounded" width={490} height={24} />
          ) : (
            <Typography fontWeight={700} fontSize={24} color="#222222">
              Users
            </Typography>
          )}
          {!isLoading && (
            <TextField
              id="outlined-start-adornment"
              placeholder="Search users"
              className="inputRounded"
              color="black"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContentPasteSearchOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </Stack>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={40} />
        ) : (
          <Button
            variant="contained"
            sx={{ borderRadius: "12px" }}
            onClick={() => navigate(`/users/manage-user/new`)}
          >
            Add User
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          pb: 1,
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 2 }}
            width="100%"
            height={400}
          />
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "#3A80F7",
                  }}
                >
                  <TableCell sx={{ color: "white", borderTopLeftRadius: 8 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>Email</TableCell>
                  <TableCell sx={{ color: "white" }}>Role</TableCell>
                  <TableCell sx={{ color: "white" }}>Personal No.</TableCell>
                  <TableCell sx={{ color: "white" }}>Office No.</TableCell>
                  <TableCell align="center" sx={{ color: "white" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: "white", borderTopRightRadius: 8 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <TableRow key={user.useruuid}>
                      <TableCell
                        sx={{
                          flex: 1,
                          opacity: user.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {user.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          flex: 1,
                          opacity: user.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {user.email}
                      </TableCell>
                      <TableCell
                        sx={{
                          flex: 1,
                          opacity: user.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {user.userrole}
                      </TableCell>
                      <TableCell
                        sx={{
                          flex: 1,
                          opacity: user.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {user.personalphone}
                      </TableCell>
                      <TableCell
                        sx={{
                          flex: 1,
                          opacity: user.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {user.officephone}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          opacity: user.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {user.isactive === 1 ? "Active" : "Inactive"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() =>
                            navigate(`/users/manage-user/${user.useruuid}`, {
                              state: { user: user },
                            })
                          }
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};
